import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogPreviewAgreementComponent } from '@app/modules/guest-experience/components/dialog-preview-agreement/dialog-preview-agreement.component';
import { LoadingState } from '@app/shared/interfaces';
import { TributeValue, TributeValueType } from '@app/shared/interfaces/lib/tribute.interface';
import { Channel } from '@app/shared/models/channel';
import { EditorApiService } from '@app/shared/services/editor/editor-api.service';
import { PlatformsService } from '@app/shared/services/platforms/platforms.service';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { SegmentEvent, SegmentIoService } from '../../../../shared/services/segmentIo/segment-io.service';
import {
  ReservationAgreementService,
  ReservationAgreementTemplate,
} from '../../services/reservation-agreements.service';
import { PlaceholderExtension } from './placeholder-extension';

@Component({
  standalone: false,
  templateUrl: './edit-reservation-agreement.component.html',
  styleUrls: ['./edit-reservation-agreement.component.scss'],
})
export class EditReservationAgreementComponent implements OnInit {
  @ViewChild('searchInput', { static: false }) searchInput: any;
  mode: 'new' | 'edit' = 'new';
  agreement: ReservationAgreementTemplate;
  confirmation_rulesets_missing_guestportal_shortcode: { id: number; name: string }[];

  hasChanged = false;
  saving = false;
  recentlySaved = false;

  combinedCodes: TributeValue[];

  searchCriteria: string;

  pageLoadingState: LoadingState;
  LoadingState = LoadingState;
  PlaceholderExtension = PlaceholderExtension;
  placeholderContent = `<span class="placeholder" text=""></span>`;

  // used by "Are you sure?" guard
  public dirty = false;

  possiblePlatforms: any[];

  triggerApiToSaveUpdates = new BehaviorSubject<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private service: ReservationAgreementService,
    private router: Router,
    private editorService: EditorApiService,
    private segmentIoService: SegmentIoService,
    private dialog: MatDialog,
    private platformsService: PlatformsService
  ) {}

  ngOnInit(): void {
    this.setModeFromRoute();
    this.fetchCustomAndShortCodes();
    this.fetchPlatforms();

    this.triggerApiToSaveUpdates
      .pipe(
        filter((shouldUpdate) => shouldUpdate),
        debounceTime(2000)
      )
      .subscribe(() => {
        if (this.mode === 'edit') {
          this.updateAgreement();
        }
      });
  }

  fetchCustomAndShortCodes() {
    this.editorService.getCustomAndShortCodes('all').subscribe((res: any) => {
      const customCodes = res.custom.filter((item, index) => res.custom.indexOf(item) === index);
      const shortCodes = res.short;
      this.combinedCodes = [];

      shortCodes.forEach((sc) => {
        this.combinedCodes.push({
          key: `${sc}`,
          value: `<span class="shortcode" contenteditable="false">%${sc}%</span>`,
          type: TributeValueType.ShortCode,
        });
      });

      customCodes.forEach((cc) => {
        this.combinedCodes.push({
          key: `${cc}`,
          value: `<span class="customcode" contenteditable="false">%%${cc}%%</span>`,
          type: TributeValueType.CustomCode,
        });
      });
    });
  }
  fetchPlatforms() {
    this.platformsService.getPlatforms().subscribe((res) => {
      if (res) {
        this.possiblePlatforms = res;
      }
    });
  }

  setModeFromRoute(): void {
    this.route.params
      .pipe(
        map((params) => {
          this.mode = params.id ? 'edit' : 'new';

          if (this.mode === 'new') {
            this.agreement = this.createDefaultModeForNew();
            this.pageLoadingState = LoadingState.Success;
          } else {
            this.pageLoadingState = LoadingState.Pending;
            this.service.fetchReservationAgreementTemplate(params.id).subscribe((res) => {
              this.agreement = res.agreement;
              this.confirmation_rulesets_missing_guestportal_shortcode =
                res.meta?.confirmation_rulesets_missing_guestportal_shortcode || [];
              this.pageLoadingState = LoadingState.Success;
            });
          }
        })
      )
      .subscribe();
  }

  editModeContentUpdated() {
    if (this.mode === 'new') {
      return;
    }

    this.triggerApiToSaveUpdates.next(true);
  }

  saveChanges() {
    if (this.mode === 'new') {
      this.createAgreement();
    } else {
      this.updateAgreement();
    }
  }

  createAgreement() {
    this.saving = true;
    this.service.createNewReservationAgreementTemplate(this.agreement).subscribe(
      (res) => {
        this.saving = false;

        if (res?.data?.uuid) {
          this.segmentIoService.track(SegmentEvent.CreateRentalAgreementTemplate, res.data);

          this.router.navigate(['/gx/rental-agreements', res.data.uuid]);
        }
      },
      () => {
        this.segmentIoService.track(SegmentEvent.CreateRentalAgreementTemplateError, {});

        this.saving = false;
      }
    );
  }

  updateAgreement() {
    this.saving = true;

    this.service.updateReservationAgreement(this.agreement).subscribe(
      (data) => {
        this.saving = false;
        this.recentlySaved = true;
        this.dirty = false;
        this.segmentIoService.track(SegmentEvent.UpdateRentalAgreementTemplate, data);

        setTimeout(() => {
          this.recentlySaved = false;
        }, 2000);
      },
      () => {
        this.segmentIoService.track(SegmentEvent.UpdateRentalAgreementTemplateError, {});

        this.saving = false;
      }
    );
  }

  createDefaultModeForNew(): ReservationAgreementTemplate {
    return {
      is_active: true,
      uuid: 'new',
      name: 'Rental Agreement',
      content: this.placeholderContent,
      version: 1,
      platforms: [Channel.Direct],
      property_ids: [],
      min_number_of_nights: null,
      max_number_of_nights: null,
    };
  }

  onContentUpdated(content: string) {
    this.hasChanged = true;
    this.agreement.content = content;
    this.dirty = true;
    this.editModeContentUpdated();
  }

  alert() {
    alert('alert');
  }

  setFocusToSearch() {
    setTimeout(() => {
      this.searchInput && this.searchInput.nativeElemenent ? this.searchInput.nativeElement.focus() : null;
    }, 100);
  }

  platformsCheckboxChange(platform, event: MatCheckboxChange) {
    if (!this.agreement.platforms) {
      this.agreement.platforms = [];
    }
    if (event.checked) {
      this.agreement.platforms.push(platform.key);
    } else {
      const index = this.agreement.platforms.findIndex((channel) => channel === platform.key);

      if (index !== -1) {
        this.agreement.platforms.splice(index, 1);
      }
    }

    this.editModeContentUpdated();
  }

  getOperatorLabel(operator) {
    let result = '';
    switch (operator) {
      case '>':
        result = 'more than';
        break;

      case '<':
        result = 'less than';
        break;

      case '=':
        result = 'exactly';
        break;

      default:
        break;
    }
    return result;
  }

  deleteAgreement() {
    this.service.deleteReservationAgreementTemplate(this.agreement.uuid).subscribe(
      (data) => {
        this.segmentIoService.track(SegmentEvent.DeleteRentalAgreementTemplate, this.agreement);

        this.router.navigate(['/gx/rental-agreements']);
      },
      () => {
        this.segmentIoService.track(SegmentEvent.DeleteRentalAgreementTemplateError, {});
      }
    );
  }

  openPreviewDialog() {
    this.dialog.open(DialogPreviewAgreementComponent, {
      width: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      data: {
        content: this.agreement.content,
      },
    });
  }
}
